import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { api } from '../lib/api';
import { isLightColor } from '../components/MobileNav';

// Login follows the theme chosen in Dashboard > Tampilan (saved in this browser).
const DEFAULT_THEME = { accent: '#E63329', accent2: '#F5C518', bg: '#0F0A0A', surface: '#160F0F' };
const readTheme = () => {
  try {
    const t = JSON.parse(localStorage.getItem('tv_dash_theme'));
    if (t && t.accent && t.bg) return { ...DEFAULT_THEME, ...t };
  } catch (e) { /* ignore */ }
  return DEFAULT_THEME;
};
const daysToEvent = () => {
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date('2026-12-12T00:00:00') - today) / 86400000);
};

export default function DashboardLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [theme] = useState(readTheme);

  const light = isLightColor(theme.bg);
  const ink = light ? '#0F172A' : '#FFFFFF';
  const muted = light ? '#64748B' : 'rgba(255,255,255,0.65)';
  const line = light ? 'rgba(15,23,42,0.14)' : 'rgba(255,255,255,0.14)';
  const field = light ? '#FFFFFF' : 'rgba(0,0,0,0.25)';
  const d = daysToEvent();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/login', { password });
      localStorage.setItem('tv_admin_token', data.token);
      navigate('/dashboard');
    } catch {
      setError('Password salah. Coba lagi.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-8" style={{ background: theme.bg, color: ink }}>
      <div className="max-w-md w-full">
        <div className="relative overflow-hidden rounded-3xl p-6 pb-20 text-white"
          style={{ background: `linear-gradient(135deg, ${theme.accent}, color-mix(in srgb, ${theme.accent} 65%, #000))` }}>
          <div aria-hidden="true" className="absolute -right-14 -top-16 w-52 h-52 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <div aria-hidden="true" className="absolute -left-10 -bottom-14 w-36 h-36 rounded-full" style={{ background: 'rgba(255,255,255,0.07)' }} />
          <div className="relative flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl grid place-items-center font-black text-lg" style={{ background: '#fff', color: theme.accent }}>T</span>
            <div>
              <div className="font-black tracking-wider">TALESVERSE</div>
              <div className="text-xs font-medium">Memories Market · Admin</div>
            </div>
          </div>
          <div className="relative flex items-end gap-2 mt-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-black leading-tight" style={{ textShadow: 'none', WebkitTextStroke: 0, color: '#fff' }}>Selamat datang!</h1>
              <p className="text-sm font-medium mt-1">Masuk untuk mengelola pendaftar, booth &amp; event.</p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.22)' }}>
                {d > 0 ? `H-${d} · 12 Des 2026` : d === 0 ? 'Hari H · 12 Des 2026' : '12 Des 2026'}
              </span>
            </div>
            <img src="/mascot/welcome.webp" alt="" className="h-36 w-auto flex-none -mb-16 -mr-2 drop-shadow-xl" />
          </div>
        </div>

        <form onSubmit={submit} className="relative -mt-12 mx-2 rounded-3xl p-5 shadow-xl"
          style={{ background: theme.surface, border: `1px solid ${line}` }}>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `color-mix(in srgb, ${theme.accent} 16%, transparent)`, color: theme.accent }}>
              <Lock className="w-5 h-5" />
            </span>
            <div>
              <div className="font-black text-lg leading-tight">Masuk Dashboard</div>
              <div className="text-xs" style={{ color: muted }}>Khusus tim organizer Talesverse</div>
            </div>
          </div>

          <label htmlFor="tv-login-password" className="block text-[13px] font-bold mb-1.5">Password</label>
          <div className="relative">
            <input
              id="tv-login-password"
              data-testid="login-password"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              placeholder="Masukkan password"
              className="w-full h-12 pl-4 pr-12 rounded-2xl text-[15px] font-medium focus:outline-none"
              style={{ background: field, color: ink, border: `1.5px solid ${error ? '#DC2626' : line}`, boxShadow: 'none' }}
              onFocus={(e) => { e.target.style.borderColor = theme.accent; }}
              onBlur={(e) => { e.target.style.borderColor = error ? '#DC2626' : line; }}
            />
            <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? 'Sembunyikan password' : 'Tampilkan password'}
              className="absolute right-1 top-1 w-10 h-10 rounded-xl grid place-items-center" style={{ color: muted }}>
              {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {error && <div role="alert" className="text-sm font-semibold mt-2" style={{ color: light ? '#B91C1C' : '#FCA5A5' }}>{error}</div>}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading || !password}
            className="w-full mt-5 h-12 rounded-2xl font-black text-[15px] text-white inline-flex items-center justify-center gap-2 transition disabled:opacity-60"
            style={{ background: theme.accent, boxShadow: `0 10px 24px color-mix(in srgb, ${theme.accent} 35%, transparent)` }}
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Masuk…</> : 'Masuk'}
          </button>
        </form>

        <div className="text-center mt-5">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold min-h-[44px] px-3" style={{ color: muted }}>
            <ArrowLeft className="w-4 h-4" /> Kembali ke website
          </Link>
        </div>
      </div>
    </div>
  );
}
